import { useInfiniteQuery } from "@tanstack/react-query";
import { redirect, createFileRoute } from "@tanstack/react-router";

import { Spinner } from "@/components/Spinner";
import { ButtonLink } from "@/components/ui/button";
import { H1, ListItem, P, UnorderedList } from "@/components/ui/typography";
import { myFeedsInfiniteQueryOptions, newsroomLoginCallback } from "@/lib/api";

export const Route = createFileRoute("/app/newsrooms/callback")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    email?: string;
    auth_token?: string;
  } => {
    // validate and parse the search params into a typed state
    return {
      email: search?.email as string | undefined,
      auth_token: search?.auth_token as string | undefined,
    };
  },
  component: SuccessPage,
  beforeLoad: async ({ search }) => {
    if (!search.auth_token || !search.email) {
      throw redirect({ to: "/login" });
    }

    await newsroomLoginCallback({
      aip_auth_token: search.auth_token,
      email_address: search.email,
    });
  },
  loader: ({ context: { queryClient } }) => {
    return queryClient.ensureInfiniteQueryData(myFeedsInfiniteQueryOptions({}));
  },
  head: () => ({ meta: [{ title: "Newsrooms" }] }),
});

function SuccessPage() {
  const { data: feeds } = useInfiniteQuery(myFeedsInfiniteQueryOptions({}));

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="p-6 pb-10 max-w-[70ch]">
        <img src="/logo.svg" alt="Graze" className="w-20 h-20 invert mb-4" />

        <H1>Welcome to Graze</H1>
        <div className="mt-6 mb-8">
          <P className="mb-4 text-lg text-slate-dim">
            You've successfully signed up for Graze.{" "}
            <span className="underline decoration-wavy underline-offset-4 decoration-pink-10 dark:decoration-pink-10">
              We're excited to have you on board.
            </span>
          </P>
          <div className="flex items-center gap-2 mb-4">
            <Spinner className="w-5 h-5 text-slate-dim" />
            <div className="text-slate-dim">
              Our backend is working hard pulling in your data, this might take
              a few minutes.
            </div>
          </div>
          <P>
            To get you started we've set up your first feed for you. This feed
            has the following features:
          </P>
          <UnorderedList className="mb-6">
            <ListItem>
              Pulls in all the content from your publication that's already on
              Bluesky
            </ListItem>
            <ListItem>
              Uses our "Trending" algorithm to see what your readers are talking
              about the most
            </ListItem>
          </UnorderedList>

          <P className="font-semibold">
            We'll be in touch soon to help you get the most out of Graze.
          </P>
        </div>
        <div className="flex gap-2">
          <ButtonLink to="/app" variant="outline">
            <div className="flex items-center gap-2">Go to Dashboard</div>
          </ButtonLink>
          <ButtonLink
            to="/app/feed/$feedId/view"
            params={{ feedId: feeds?.pages[0]?.data[0]?.id?.toString() }}
          >
            <div className="flex items-center gap-2">View Your Feed</div>
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}
